import { DAY_MS } from "./algorithms";
import { addressBytes } from "./names";

// A host name, a wildcard standing in for its first label, or a literal address: what a certificate can name.
export function isHostOrAddress(value: string): boolean {
  return isAddress(value) || isHostName(value);
}

export function isHostName(value: string): boolean {
  if (value.length > 253) return false;
  const labels = value.split(".");
  // A star replaces one whole label and only the leading one, which is as far as any client will match it.
  if (labels[0] === "*") labels.shift();
  if (labels.length === 0 || !labels.every((label) => HOST_LABEL.test(label))) return false;
  // Ending in a number is how a mistyped address looks; a real one has already been read as an address above.
  return !/^\d+$/.test(labels[labels.length - 1]);
}

// One label of a host name: letters, digits and inner hyphens, up to the 63 characters DNS gives a label.
const HOST_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

// Whether it is an address is whether it can be written as one, which is the only reading that matters to a
// certificate: what goes into a subject alternative name is bytes, and a text a name cannot be built from is not one.
export function isAddress(value: string): boolean {
  return addressBytes(value) !== null;
}

// Whitespace or commas, so a list can be pasted in whichever of the two spellings it was written in.
export function splitAltNames(value: string): string[] {
  return value.split(/[,\s]+/).filter((entry) => entry !== "");
}

// Two letters and nothing else, which is what a PrintableString country attribute is allowed to hold.
export function isCountry(value: string): boolean {
  return /^[A-Za-z]{2}$/.test(value);
}

export function parseWhole(value: number | string, max: number): number | null {
  const parsed = typeof value === "number" ? value : parseInt(value, 10);
  if (!Number.isFinite(parsed)) return null;
  const rounded = Math.floor(parsed);
  return rounded >= 1 && rounded <= max ? rounded : null;
}

// A shared link carries anything at all, so this lands on a usable setting rather than trust it.
export function clampWhole(value: unknown, fallback: number, max: number): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(1, Math.floor(value)));
}

// The date a number of days is really naming, which is the thing anybody setting it is trying to land on.
export function expiryLabel(days: number): string {
  return `Until ${new Date(Date.now() + days * DAY_MS).toLocaleDateString()}`;
}

export function message(e: unknown): string {
  return e instanceof Error ? e.message : "This certificate could not be made";
}
